/**
 * Report USER-STATE — the reader's decisions on an otherwise immutable report.
 *
 * Immutable-v2 reports serve their generated body from a content-addressed
 * revision object that the client may not repoint (firestore.rules
 * `preservesServerReportState`). Reader decisions — today the sanity
 * findings' Apply-on-rerun / Ignore + note / Withdrawn — therefore need a home
 * of their own. Before 2026-08-18 they were written into the owner-namespace
 * legacy body object, which nothing read back (see reportBodyHydration
 * `overlaySanityDecisions`, kept as the read-only historical fallback).
 *
 * This module defines that home: ONE Firestore document per report,
 *
 *   users/{ownerUid}/reports/{reportId}/userState/current
 *
 * keyed by finding IDENTITY (the same section+category+text fingerprint the
 * UD4 preservation pass uses), not by array position — so a decision survives
 * a re-ordered or partially re-phrased findings list, and an unrelated finding
 * can never inherit it. Pure: no Firestore imports; both the client store and
 * the server job import it.
 */
import { fingerprintFinding } from './findingFingerprint';
import {
  isVerifiedSanityCategory,
  removeApprovedFactualCorrection,
  upsertApprovedFactualCorrection,
  type ApprovedFactualCorrectionsLedgerV1,
} from './preservedUserDecisions';

export const REPORT_USER_STATE_SCHEMA_VERSION = 'report-user-state-v1' as const;
export const REPORT_USER_STATE_DOC_ID = 'current' as const;

export type SanityDecisionResolution = 'apply' | 'ignore' | 'withdrawn';

export interface SanityDecisionEntryV1 {
  /** Human-readable identity this key was derived from (debugging / audit). */
  identity: string;
  resolution: SanityDecisionResolution;
  userNote?: string;
  /** Snapshot of what to apply — present only for `resolution === 'apply'`. */
  applyDirective?: unknown;
  /** Metadata stored for approved factual corrections and withdrawal tombstones. */
  section?: string;
  category?: string;
  finding?: string;
  evidenceBaseline?: string;
  evidenceSourceFields?: Record<string, string | number | boolean | null>;
  evidenceSourceIds?: string[];
  evidenceSourceRevision?: string | number;
  correctionVersion?: number;
  resolvedAt: string;
  resolvedByUid: string;
}

export interface ReportUserStateV1 {
  schemaVersion: typeof REPORT_USER_STATE_SCHEMA_VERSION;
  reportId: string;
  /** Map keyed by `sanityDecisionKey(identity)`. */
  sanityDecisions: Record<string, SanityDecisionEntryV1>;
  updatedAt: string;
  updatedByUid: string;
}

/** Firestore doc path of the report's user-state document. */
export function reportUserStateDocPath(ownerUid: string, reportId: string): string {
  return `users/${ownerUid}/reports/${reportId}/userState/${REPORT_USER_STATE_DOC_ID}`;
}

/** Fields a finding carries that identify it across reruns. */
export interface SanityFindingIdentityInput {
  id?: string;
  finding?: unknown;
  category?: unknown;
  section?: unknown;
  evidence?: unknown;
  evidenceSourceFields?: Record<string, string | number | boolean | null>;
  evidenceSourceIds?: string[];
  evidenceSourceRevision?: string | number;
  suggestion?: unknown;
}

/**
 * The SAME identity the UD4 preservation pass uses
 * (siteRagService `mergePreservedUserDecisions` → `fingerprintFinding`), so a
 * decision keyed here is exactly the decision UD4 would carry forward.
 */
export function sanityFindingIdentity(f: SanityFindingIdentityInput): string {
  const text = typeof f.finding === 'string' ? f.finding : '';
  const category = typeof f.category === 'string' ? f.category : '';
  const section = typeof f.section === 'string' ? f.section : '';
  return `${section}::${category}::${fingerprintFinding(text, { category, section })}`;
}

/**
 * Firestore-safe map key for an identity string (which contains `|`, `::` and
 * spaces). FNV-1a 32-bit, hex, dependency-free so the browser store can compute
 * it too. Collision space is one report's ≤ ~30 findings — negligible, and the
 * entry keeps the full identity for verification.
 */
export function sanityDecisionKey(identity: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < identity.length; i++) {
    h ^= identity.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `f${h.toString(16).padStart(8, '0')}`;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Structural guard for a doc read back from Firestore. */
export function isReportUserStateV1(v: unknown): v is ReportUserStateV1 {
  return isRecord(v)
    && v.schemaVersion === REPORT_USER_STATE_SCHEMA_VERSION
    && typeof v.reportId === 'string'
    && isRecord(v.sanityDecisions);
}

/** Decision fields carried onto a finding. Mirrors SANITY_DECISION_FIELDS. */
const CARRIED: ReadonlyArray<'resolution' | 'userNote' | 'applyDirective' | 'resolvedAt'> =
  ['resolution', 'userNote', 'applyDirective', 'resolvedAt'];

/**
 * Overlay identity-keyed decisions onto a report's `sanityFindings` and
 * `approvedFactualCorrections` ledger.
 *
 * - A finding whose identity has no entry is untouched; an entry whose stored
 *   `identity` disagrees with the key's finding is skipped (fail closed).
 * - A `'withdrawn'` decision strips any frozen `resolution`, `userNote`,
 *   `applyDirective`, `resolvedAt`, and `correctionLineage` from the finding
 *   AND writes a `status: 'withdrawn'` marker in `approvedFactualCorrections`
 *   that overrides the frozen report ledger on reload and subsequent reruns.
 * - Ledger reconciliation also runs for decisions whose finding is no longer
 *   present in `base.sanityFindings` (e.g. a V1 correction that resolved in V2
 *   and was then withdrawn in V2).
 */
export function overlaySanityDecisionMap<
  T extends { sanityFindings?: unknown; id?: unknown; approvedFactualCorrections?: ApprovedFactualCorrectionsLedgerV1 },
>(
  base: T,
  userState: unknown,
): T {
  if (!isReportUserStateV1(userState)) return base;
  const findings = Array.isArray(base.sanityFindings) ? base.sanityFindings : null;
  let findingsChanged = false;
  let ledgerChanged = false;
  let ledger: ApprovedFactualCorrectionsLedgerV1 | undefined = base.approvedFactualCorrections;
  const reportId = typeof base.id === 'string' ? base.id : userState.reportId;
  const matchedFindingsByIdentity = new Map<string, Record<string, unknown>>();
  const handledLedgerIdentities = new Set<string>();

  const merged = findings
    ? findings.map((entry) => {
        if (!isRecord(entry)) return entry;
        const identity = sanityFindingIdentity(entry);
        matchedFindingsByIdentity.set(identity, entry);
        const decision = userState.sanityDecisions[sanityDecisionKey(identity)];
        if (!isRecord(decision) || decision.identity !== identity) return entry;
        const isVerified = isVerifiedSanityCategory(entry.category ?? decision.category);
        const rawRes = decision.resolution as SanityDecisionResolution | undefined;

        if (rawRes === 'withdrawn') {
          handledLedgerIdentities.add(identity);
          const hadDecisionFields =
            entry.resolution !== undefined ||
            entry.userNote !== undefined ||
            entry.applyDirective !== undefined ||
            entry.resolvedAt !== undefined ||
            entry.correctionLineage !== undefined;
          const nextEntry = { ...entry };
          delete nextEntry.resolution;
          delete nextEntry.userNote;
          delete nextEntry.applyDirective;
          delete nextEntry.resolvedAt;
          delete nextEntry.correctionLineage;
          if (hadDecisionFields) {
            findingsChanged = true;
          }
          if (!isVerified) {
            const nextLedger = removeApprovedFactualCorrection(
              ledger,
              identity,
              typeof decision.resolvedAt === 'string' ? decision.resolvedAt : userState.updatedAt,
              {
                section: typeof entry.section === 'string' ? entry.section : (typeof decision.section === 'string' ? decision.section : 'General'),
                category: typeof entry.category === 'string' ? entry.category : (typeof decision.category === 'string' ? decision.category : 'inconsistency'),
                finding: typeof entry.finding === 'string' ? entry.finding : (typeof decision.finding === 'string' ? decision.finding : identity),
                originReportId: reportId,
                withdrawnByUid: typeof decision.resolvedByUid === 'string' ? decision.resolvedByUid : userState.updatedByUid,
              },
            );
            if (nextLedger !== ledger) {
              ledger = nextLedger;
              ledgerChanged = true;
            }
          }
          return hadDecisionFields ? nextEntry : entry;
        }

        const effectiveResolution: 'apply' | 'ignore' | undefined =
          rawRes === 'apply' && isVerified ? 'ignore' : (rawRes as 'apply' | 'ignore' | undefined);
        if (!effectiveResolution) return entry;

        const nextEntry: Record<string, unknown> = { ...entry };
        let entryMutated = false;

        for (const field of CARRIED) {
          if (field === 'resolution') {
            if (nextEntry.resolution !== effectiveResolution) {
              nextEntry.resolution = effectiveResolution;
              entryMutated = true;
            }
            continue;
          }
          if (field === 'applyDirective' && effectiveResolution !== 'apply') {
            if (nextEntry.applyDirective !== undefined) {
              delete nextEntry.applyDirective;
              entryMutated = true;
            }
            continue;
          }
          if (field === 'userNote' && effectiveResolution === 'ignore' && !isVerified) {
            if (nextEntry.userNote !== undefined) {
              delete nextEntry.userNote;
              entryMutated = true;
            }
            continue;
          }
          if (decision[field] !== undefined && nextEntry[field] !== decision[field]) {
            nextEntry[field] = decision[field];
            entryMutated = true;
          }
        }

        if (entryMutated) {
          findingsChanged = true;
        }

        handledLedgerIdentities.add(identity);
        if (effectiveResolution === 'apply' && !isVerified) {
          const existingLedgerEntry = ledger?.entries?.[identity];
          const directiveCandidate =
            (typeof nextEntry.userNote === 'string' && nextEntry.userNote.trim()) ||
            (isRecord(nextEntry.applyDirective) && typeof nextEntry.applyDirective.suggestionAtResolution === 'string'
              ? nextEntry.applyDirective.suggestionAtResolution.trim()
              : '') ||
            (typeof nextEntry.suggestion === 'string' ? nextEntry.suggestion.trim() : '') ||
            (typeof nextEntry.finding === 'string' ? nextEntry.finding.trim() : '');
          const needsUpsert =
            !existingLedgerEntry ||
            existingLedgerEntry.status !== 'active' ||
            existingLedgerEntry.appliedDirective !== directiveCandidate ||
            existingLedgerEntry.userNote !== (typeof nextEntry.userNote === 'string' ? nextEntry.userNote : undefined);
          if (needsUpsert) {
            const nextLedger = upsertApprovedFactualCorrection(ledger, {
              identity,
              finding: nextEntry as unknown as import('../types/siteRag').SanityFinding,
              userNote: typeof nextEntry.userNote === 'string' ? nextEntry.userNote : undefined,
              applyDirective: nextEntry.applyDirective as import('../types/siteRag').SanityFinding['applyDirective'],
              approvedAt: typeof decision.resolvedAt === 'string' ? decision.resolvedAt : userState.updatedAt,
              approvedByUid: typeof decision.resolvedByUid === 'string' ? decision.resolvedByUid : userState.updatedByUid,
              originReportId: reportId,
            });
            if (nextLedger !== ledger) {
              ledger = nextLedger;
              ledgerChanged = true;
            }
          }
        } else if (effectiveResolution === 'ignore' && !isVerified && ledger?.entries?.[identity]) {
          const nextLedger = removeApprovedFactualCorrection(
            ledger,
            identity,
            typeof decision.resolvedAt === 'string' ? decision.resolvedAt : userState.updatedAt,
            {
              section: typeof nextEntry.section === 'string' ? nextEntry.section : 'General',
              category: typeof nextEntry.category === 'string' ? nextEntry.category : 'inconsistency',
              finding: typeof nextEntry.finding === 'string' ? nextEntry.finding : identity,
              originReportId: reportId,
              withdrawnByUid: typeof decision.resolvedByUid === 'string' ? decision.resolvedByUid : userState.updatedByUid,
            },
          );
          if (nextLedger !== ledger) {
            ledger = nextLedger;
            ledgerChanged = true;
          }
        }

        return entryMutated ? nextEntry : entry;
      })
    : null;

  // Also reconcile any userState decisions whose finding identity was NOT present
  // in `base.sanityFindings` (for example, a correction from V1 that resolved in
  // V2 so V2.sanityFindings no longer lists it, and the user withdraws it in V2).
  for (const [key, rawDecision] of Object.entries(userState.sanityDecisions)) {
    if (!isRecord(rawDecision) || typeof rawDecision.identity !== 'string') continue;
    const identity = rawDecision.identity;
    if (sanityDecisionKey(identity) !== key) continue;
    if (handledLedgerIdentities.has(identity)) continue;

    const existingLedgerEntry = ledger?.entries?.[identity];
    const category =
      (typeof rawDecision.category === 'string' ? rawDecision.category : undefined) ??
      existingLedgerEntry?.category ??
      'inconsistency';
    const isVerified = isVerifiedSanityCategory(category);
    if (isVerified) continue;

    const rawRes = rawDecision.resolution as SanityDecisionResolution | undefined;
    if (rawRes === 'withdrawn' || (rawRes === 'ignore' && existingLedgerEntry)) {
      const nextLedger = removeApprovedFactualCorrection(
        ledger,
        identity,
        typeof rawDecision.resolvedAt === 'string' ? rawDecision.resolvedAt : userState.updatedAt,
        {
          section: typeof rawDecision.section === 'string' ? rawDecision.section : (existingLedgerEntry?.section || 'General'),
          category,
          finding: typeof rawDecision.finding === 'string' ? rawDecision.finding : (existingLedgerEntry?.finding || identity),
          originReportId: reportId,
          withdrawnByUid: typeof rawDecision.resolvedByUid === 'string' ? rawDecision.resolvedByUid : userState.updatedByUid,
        },
      );
      if (nextLedger !== ledger) {
        ledger = nextLedger;
        ledgerChanged = true;
      }
    } else if (rawRes === 'apply' && existingLedgerEntry) {
      const syntheticFinding: import('../types/siteRag').SanityFinding = {
        id: identity,
        section: typeof rawDecision.section === 'string' ? rawDecision.section : (existingLedgerEntry?.section || 'General'),
        category: category as import('../types/siteRag').SanityFinding['category'],
        severity: 'medium',
        finding: typeof rawDecision.finding === 'string' ? rawDecision.finding : (existingLedgerEntry?.finding || identity),
        suggestion:
          (isRecord(rawDecision.applyDirective) && typeof rawDecision.applyDirective.suggestionAtResolution === 'string'
            ? rawDecision.applyDirective.suggestionAtResolution
            : '') ||
          existingLedgerEntry?.appliedDirective ||
          (typeof rawDecision.userNote === 'string' ? rawDecision.userNote : ''),
        ...(typeof rawDecision.evidenceBaseline === 'string' ? { evidence: rawDecision.evidenceBaseline } : {}),
        ...(isRecord(rawDecision.evidenceSourceFields)
          ? { evidenceSourceFields: rawDecision.evidenceSourceFields as Record<string, string | number | boolean | null> }
          : {}),
        ...(Array.isArray(rawDecision.evidenceSourceIds)
          ? { evidenceSourceIds: rawDecision.evidenceSourceIds.filter((s): s is string => typeof s === 'string') }
          : {}),
        ...(typeof rawDecision.evidenceSourceRevision === 'string' || typeof rawDecision.evidenceSourceRevision === 'number'
          ? { evidenceSourceRevision: rawDecision.evidenceSourceRevision }
          : {}),
      };
      const nextLedger = upsertApprovedFactualCorrection(ledger, {
        identity,
        finding: syntheticFinding,
        userNote: typeof rawDecision.userNote === 'string' ? rawDecision.userNote : undefined,
        applyDirective: rawDecision.applyDirective as import('../types/siteRag').SanityFinding['applyDirective'],
        approvedAt: typeof rawDecision.resolvedAt === 'string' ? rawDecision.resolvedAt : userState.updatedAt,
        approvedByUid: typeof rawDecision.resolvedByUid === 'string' ? rawDecision.resolvedByUid : userState.updatedByUid,
        originReportId: reportId,
      });
      if (nextLedger !== ledger) {
        ledger = nextLedger;
        ledgerChanged = true;
      }
    }
  }

  if (!findingsChanged && !ledgerChanged) return base;
  return {
    ...base,
    ...(findingsChanged && merged ? { sanityFindings: merged } : {}),
    ...(ledger !== undefined ? { approvedFactualCorrections: ledger } : {}),
  };
}

/**
 * Build the entry for a decision the reader just made.
 *
 * - `next.resolution === 'apply'` (on non-verified findings): approved factual
 *   correction with `userNote`, `applyDirective`, and traceable baseline metadata.
 * - `next.resolution === 'ignore'` on verified findings (`risk-verified` /
 *   `strength-verified`): acknowledgement with optional edited `userNote`,
 *   never `applyDirective`.
 * - `next.resolution === 'ignore'` on non-verified findings: ignored issue,
 *   dropping `userNote` and `applyDirective`.
 * - `next.resolution === 'withdrawn'` or `undefined`: explicit withdrawal
 *   marker persisted in `userState.sanityDecisions` so clearing a correction
 *   overrides the frozen report ledger on reload and subsequent reruns.
 */
export function buildSanityDecisionEntry(
  finding: SanityFindingIdentityInput,
  next: { resolution?: SanityDecisionResolution; userNote?: string; applyDirective?: unknown },
  uid: string,
  now: string = new Date().toISOString(),
): { key: string; entry: SanityDecisionEntryV1 } {
  const identity = sanityFindingIdentity(finding);
  const isVerified = isVerifiedSanityCategory(finding.category);
  const rawResolution: SanityDecisionResolution = next.resolution ?? 'withdrawn';
  const effectiveResolution: SanityDecisionResolution =
    rawResolution === 'apply' && isVerified ? 'ignore' : rawResolution;
  const trimmedNote = typeof next.userNote === 'string' && next.userNote.trim().length > 0
    ? next.userNote.trim()
    : undefined;
  const shouldKeepUserNote =
    Boolean(trimmedNote) && (effectiveResolution === 'apply' || (effectiveResolution === 'ignore' && isVerified));

  const entry: SanityDecisionEntryV1 = {
    identity,
    resolution: effectiveResolution,
    resolvedAt: now,
    resolvedByUid: uid,
    ...(shouldKeepUserNote ? { userNote: trimmedNote } : {}),
    ...(effectiveResolution === 'apply' && next.applyDirective !== undefined
      ? { applyDirective: next.applyDirective }
      : {}),
    ...(typeof finding.section === 'string' && finding.section.length > 0
      ? { section: finding.section }
      : {}),
    ...(typeof finding.category === 'string' && finding.category.length > 0
      ? { category: finding.category }
      : {}),
    ...(typeof finding.finding === 'string' && finding.finding.length > 0
      ? { finding: finding.finding }
      : {}),
    ...(effectiveResolution === 'apply' && typeof finding.evidence === 'string' && finding.evidence.trim().length > 0
      ? { evidenceBaseline: finding.evidence.trim() }
      : {}),
    ...(effectiveResolution === 'apply' && isRecord(finding.evidenceSourceFields)
      ? { evidenceSourceFields: { ...finding.evidenceSourceFields } }
      : {}),
    ...(effectiveResolution === 'apply' && Array.isArray(finding.evidenceSourceIds)
      ? { evidenceSourceIds: [...finding.evidenceSourceIds] }
      : {}),
    ...(effectiveResolution === 'apply' &&
    (typeof finding.evidenceSourceRevision === 'string' || typeof finding.evidenceSourceRevision === 'number')
      ? { evidenceSourceRevision: finding.evidenceSourceRevision }
      : {}),
    ...(effectiveResolution === 'apply' ? { correctionVersion: 1 } : {}),
  };
  return { key: sanityDecisionKey(identity), entry };
}
